import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, OverlayTrigger, Row, Table as RBTable, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import {
  PluginHook,
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  Row as RTRow,
} from 'react-table';
import { matchSorter } from 'match-sorter';

import OverlaySpinner from '../OverlaySpinner/OverlaySpinner';
import { TableDataTypes } from '../../../utils/constants';

import './Table.scss';

interface TableColumn {
  Header: string;
  accessor: string;
  filterAccessor?: string;
  isId?: boolean;
  className?: string;
  // TODO - we should make this a required field so that a data type
  // can always be expected.
  dataType?: TableDataTypes;
}
interface TableData {
  [accessor: string]: string | IUser;
}

interface TableIProps {
  activeRowId?: string;
  columns: TableColumn[];
  customDataForCell?: any;
  data?: TableData[] | IUser[];
  hiddenColumns?: string[];
  initialSortColumnId?: string;
  tableTitle?: string;
  rowsNotClickable?: boolean;
  onRowClick?: (data: any) => void;
  noPaging?: boolean;
  pageSize?: number;
}

export const Table = ({
  activeRowId,
  columns,
  customDataForCell,
  data,
  hiddenColumns,
  initialSortColumnId,
  tableTitle,
  rowsNotClickable,
  onRowClick,
  noPaging,
  pageSize = 10,
}: TableIProps) => {
  const memoizedData = useMemo(() => data, [data]) as TableData[];
  const memoizedColumns = useMemo(() => columns, [columns]);
  const initialSortedColumn = useMemo(() => {
    if (memoizedColumns && memoizedColumns.length) {
      return [
        {
          id: memoizedColumns[0].accessor,
        },
      ];
    }
    return [];
  }, [memoizedColumns]);

  const tablePlugins: PluginHook<TableData>[] = [useGlobalFilter, useSortBy];

  if (!noPaging) {
    tablePlugins.push(usePagination);
  }

  const globalFilter = useMemo(
    () =>
      (rows: RTRow<TableData>[], columnIds: string[], filterValue: string): RTRow<TableData>[] => {
        const fieldNames: string[] = [];
        for (const columnId of columnIds) {
          const column = memoizedColumns.find((c) => c.accessor === columnId);
          if (column?.filterAccessor) {
            fieldNames.push(column.filterAccessor);
          } else {
            fieldNames.push(columnId);
          }
        }

        const filteredRows = matchSorter(rows, filterValue, {
          keys: fieldNames.map((f) => `original.${f}`),
          threshold: matchSorter.rankings.CONTAINS,
        });

        return filteredRows;
      },
    [memoizedColumns],
  );

  const {
    canNextPage,
    canPreviousPage,
    headerGroups,
    rows,
    page,
    pageOptions,
    state,
    getTableBodyProps,
    getTableProps,
    nextPage,
    prepareRow,
    previousPage,
    gotoPage,
    setGlobalFilter,
    setHiddenColumns,
  } = useTable(
    {
      columns: memoizedColumns,
      data: memoizedData || [],
      disableMultiSort: true,
      initialState: {
        sortBy: initialSortedColumn,
        hiddenColumns: hiddenColumns || [],
        pageSize,
      },
      autoResetHiddenColumns: false,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      globalFilter,
    },
    ...tablePlugins,
  );

  const { pageIndex } = state;
  const [searchValue, setSearchValue] = useState('');

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);

    // Reset to first page if paging is enabled
    if (!noPaging) {
      gotoPage(0);
    }
  }, 200);

  useEffect(() => {
    if (hiddenColumns) {
      setHiddenColumns(hiddenColumns);
    } else {
      setHiddenColumns([]);
    }
  }, [hiddenColumns, setHiddenColumns]);

  if (!data) {
    return <OverlaySpinner />;
  }

  return (
    <div className="Table">
      <div className="Table-wrapper w-100">
        <Row className="mb-2">
          <Col xs={8} className="Table-title text-left">
            {tableTitle && (
              <h5>
                {tableTitle} ({(memoizedData || []).length})
              </h5>
            )}
          </Col>
          <Col xs={4} className="Table-search">
            <Form.Control
              placeholder="Search..."
              value={searchValue}
              onChange={(event: any) => {
                setSearchValue(event?.target.value);
                onSearchChange(event?.target.value);
              }}
            />
          </Col>
        </Row>
        <RBTable {...getTableProps()} className="Table-main" responsive>
          <thead>
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {/* Dev Note: casting column to any to get extra props. */}
                  {/* We should extend the type and add our own props properly */}
                  {headerGroup.headers.map((column: any) => {
                    return (
                      <th
                        {...column.getHeaderProps({
                          ...column.getSortByToggleProps(),
                          style: {
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                            textOverflow: column.textOverflow,
                            overflow: column.overflow,
                          },
                        })}
                        className={classNames('Table-column-header align-items-center', {
                          sorted: column.isSorted,
                        })}
                      >
                        {' '}
                        <div className="Table-header d-flex align-items-center justify-content-between">
                          <div className="Table-header-left-content d-flex align-items-center">
                            {/* Header */}
                            <div className="Table-header-name">{column.render('Header')}</div>
                            <span className="material-icons Table-header-sort-icon">
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? 'arrow_downward'
                                  : 'arrow_upward'
                                : ''}
                            </span>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(noPaging ? rows : page)?.map((row: any, i) => {
              prepareRow(row);
              const { _id, isChanged, markedForRemoval, hasError, errors } = row.original;
              const tableRow = (
                <tr
                  {...row.getRowProps()}
                  onClick={onRowClick && !rowsNotClickable ? () => onRowClick(row) : undefined}
                  className={classNames({
                    'clickable-row': onRowClick && !rowsNotClickable,
                    'active-row': activeRowId && _id === activeRowId,
                    'row-marked-for-removal': markedForRemoval || hasError,
                    'changed-row': isChanged,
                  })}
                >
                  {/* Dev Note: casting column to any to get extra props. */}
                  {/* We should extend the type and add our own props properly */}
                  {row.cells.map((cell: any) => {
                    return (
                      <td
                        {...cell.getCellProps({
                          style: {
                            minWidth: cell.column.minWidth,
                            maxWidth: cell.column.maxWidth,
                            textOverflow: cell.column.textOverflow,
                            overflow: cell.column.overflow,
                          },
                        })}
                      >
                        {cell.render('Cell', { customDataForCell: customDataForCell })}
                      </td>
                    );
                  })}
                </tr>
              );

              if (errors?.length) {
                return (
                  <OverlayTrigger
                    overlay={(props) => (
                      <Tooltip id={_id || `row-${i}`} {...props}>
                        {errors.map((e: string) => (
                          <div>{e}</div>
                        ))}
                      </Tooltip>
                    )}
                    placement="left"
                  >
                    {tableRow}
                  </OverlayTrigger>
                );
              }

              return tableRow;
            })}
          </tbody>
        </RBTable>
      </div>
      {/* Pagination UI */}
      {!noPaging && (
        <div role="group" aria-label="Table Page Controls">
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>{`<`}</button>
          <span className="mx-2">{`Page ${pageIndex + 1} of ${pageOptions.length}`}</span>
          <button onClick={() => nextPage()} disabled={!canNextPage}>{`>`}</button>
        </div>
      )}
    </div>
  );
};

export default Table;
